import { useState } from 'react';

import {
    TFormalOffer,
    TFormalOffers
} from '@helpers/FormalOffers/formalOffers.types';
import { IconAngleLeft, IconCalculator, IconTimesDefault } from '@iwoca/icons';
import { Button, Modal } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import { CompareOffersTable } from './CompareOffersTable/CompareOffersTable';
import { RepaymentCalculator } from './RepaymentCalculator/RepaymentCalculator';

type TComparisonAndRepaymentModalProps = {
    onClose: () => void;
    isModalOpen: boolean;
    formalOffers: TFormalOffers;
    drawdownAmount: number;
    selectedOffer: TFormalOffer;
};

type TCompareOffersViewProps = Pick<
    TComparisonAndRepaymentModalProps,
    'formalOffers' | 'drawdownAmount'
> & {
    onOpenRepaymentCalculator: () => void;
};

type TRepaymentCalculatorViewProps = Pick<
    TComparisonAndRepaymentModalProps,
    'selectedOffer' | 'drawdownAmount'
>;

const CompareOffersView = ({
    formalOffers,
    drawdownAmount,
    onOpenRepaymentCalculator
}: TCompareOffersViewProps) => (
    <>
        <Text className="mb-l mt-2xl text-2xl font-bold">
            <FormattedMessage
                id="_.compare_offers_table_modal.title"
                defaultMessage="Compare loan options"
            />
        </Text>

        <CompareOffersTable
            formalOffers={formalOffers}
            drawdownAmount={drawdownAmount}
        />

        <div className="mt-l flex flex-wrap items-center gap-s rounded-s bg-primary-97 p-m">
            <IconCalculator />

            <Text className="text-s">
                <FormattedMessage
                    id="_.compare_offers_table_modal.no_early_repayment_fees"
                    defaultMessage="Remember, no early repayment fees."
                />
            </Text>
            <Button
                variant="tertiary"
                className="h-auto p-0"
                size="small"
                onClick={onOpenRepaymentCalculator}
            >
                <FormattedMessage
                    id="_.compare_offers_table_modal.calculate_potential_savings"
                    defaultMessage="Calculate your potential savings"
                />
            </Button>
        </div>
    </>
);

const RepaymentCalculatorView = ({
    selectedOffer,
    drawdownAmount
}: TRepaymentCalculatorViewProps) => (
    <>
        <Text className="mb-l mt-2xl text-2xl font-bold">
            <FormattedMessage
                id="_.repayment_calculator_modal.title"
                defaultMessage="Calculate early repayment savings"
            />
        </Text>

        <RepaymentCalculator
            selectedOffer={selectedOffer}
            drawdownAmount={drawdownAmount}
        />
    </>
);

export const ComparisonAndRepaymentModal = ({
    onClose,
    isModalOpen,
    formalOffers,
    drawdownAmount,
    selectedOffer
}: TComparisonAndRepaymentModalProps) => {
    const [view, setView] = useState<'compareOffers' | 'repaymentCalculator'>(
        'compareOffers'
    );

    return (
        <Modal
            headerBar={true}
            onClose={onClose}
            isOpen={isModalOpen}
            size="large"
        >
            <div
                className={`absolute left-0 right-m top-0 z-10 flex items-center justify-between bg-structure-100 py-m pl-l pr-s`}
            >
                {view === 'repaymentCalculator' ? (
                    <button
                        className="h-auto p-0 text-structure-0"
                        onClick={() => setView('compareOffers')}
                        data-testid="back-to-compare-offers"
                    >
                        <IconAngleLeft fontSize="24px" />
                    </button>
                ) : (
                    <div />
                )}
                <button
                    className="h-auto p-0 text-structure-0"
                    onClick={onClose}
                    data-testid="close-comparison-and-repayment-modal"
                >
                    <IconTimesDefault fontSize="24px" />
                </button>
            </div>

            {view === 'compareOffers' ? (
                <CompareOffersView
                    formalOffers={formalOffers}
                    drawdownAmount={drawdownAmount}
                    onOpenRepaymentCalculator={() =>
                        setView('repaymentCalculator')
                    }
                />
            ) : (
                <RepaymentCalculatorView
                    selectedOffer={selectedOffer}
                    drawdownAmount={drawdownAmount}
                />
            )}
        </Modal>
    );
};
